 import styles from '@styles/components/General/Button.module.css';
import { RefreshOutlined } from '@mui/icons-material';
import { useState } from 'react';

interface ButtonProps {
    text: string;
    onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
    disabled?: boolean;
    loading?: boolean;
    hollowed?: boolean;
    backgroundColor?: string;
    textColor?: string;
    borderColor?: string;
    children?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({ 
		text, 
		onClick,
		disabled = false,
		loading = false,
		backgroundColor, 
		textColor,
    borderColor,
    children
	}) => {
	const customStyle = {
		backgroundColor: backgroundColor,
		color: textColor,
		borderColor: borderColor,
	};

	const disabledStyle = {
		backgroundColor: 'transparent',
		color: '#C8C8C8',
		border: '1px solid #C8C8C8',
		cursor: "not-allowed",
	};

	// When loading, don't apply the disabledStyle
	const appliedStyle = disabled && !loading ? disabledStyle : customStyle;

	return (
		<div 
			className={styles.button} 
			onClick={disabled || loading ? () => {} : onClick} 
			style={appliedStyle}
		>
            {children}
			{loading && <span className={styles.skeleton}></span>}
			{!loading && text}
		</div>
	);
};


interface SpecializedButtonProps extends Omit<ButtonProps, 'backgroundColor' | 'textColor' | 'borderColor'> {}

export const HollowedButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            backgroundColor='transparent'
            textColor='#EC6C56'
            borderColor='#EC6C56'
        />
    );
};


export const NormalButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            backgroundColor='#EC6C56'
            textColor='white'
            borderColor='#EC6C56'
        />
    );
};


export const TintedButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            backgroundColor='#ec6c561a'
            textColor='#EC6C56'
            borderColor='#ec6c561a'
        />
    );
};

export const GrayButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            backgroundColor='#F4F4F4'
            textColor='#666666'
            borderColor='#F4F4F4'
        />
    );
};

export const GhostButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            backgroundColor='transparent'
            textColor='#EC6C56'
            borderColor='#F4F4F4'
        />
    );
};


export const RefreshButton: React.FC<SpecializedButtonProps> = (props) => {
    const [isRotating, setIsRotating] = useState(false);

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        setIsRotating(true);
        props.onClick?.(event);
        setTimeout(() => setIsRotating(false), 500);
    };

    return (
        <Button
            {...props}
            backgroundColor='transparent'
            textColor='#EC6C56'
            borderColor='transparent'
            onClick={handleClick}
        >
            <div className={`${styles['refresh-button']} ${isRotating ? styles.rotate : ''}`}>
                <RefreshOutlined style={{ color: '#EC6C56', fontSize: '20px' }} />
            </div>
        </Button>
    );
};

export const BigRefreshButton: React.FC<SpecializedButtonProps> = (props) => {
    const [isRotating, setIsRotating] = useState(false);

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        setIsRotating(true);
        props.onClick?.(event);
        setTimeout(() => setIsRotating(false), 500);
    };

    return (
        <Button
            {...props}
            backgroundColor='#FFEAE6'
            textColor='#EC6C56'
            borderColor='transparent'
            onClick={handleClick}
        >
            <div className={`${styles['refresh-button']} ${isRotating ? styles.rotate : ''}`}>
                <RefreshOutlined style={{ color: '#EC6C56', fontSize: '20px' }} />
            </div>
        </Button>
    );
};

export default Button;
